import {
  AmbientLight,
  BatchedMesh,
  Box3,
  BufferAttribute,
  BufferGeometry,
  Camera,
  DirectionalLight,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Mesh,
  InstancedMesh,
  LinearFilter,
  MeshLambertMaterial,
  Quaternion,
  RepeatWrapping,
  SRGBColorSpace,
  Scene,
  Texture,
  Uint32BufferAttribute,
  Vector3,
  WebGLRenderer,
  Matrix4,
} from "three";
import { Observable, observe } from "../../utils/observe";
import { Viewer } from "../viewer";
import { devLog } from "../../utility/devLog";

// ── Design source ───────────────────────────────────────────────────────

/** Formats the design scene can hold. Only "ifc" and "scm" have a loader. */
export type DesignFormat = "ifc" | "scm" | "gltf" | "fbx";

export type DesignSource = { url: string; format: DesignFormat };

// ── SCM Types ───────────────────────────────────────────────────────────

interface SCMGeometryDesc {
  vertexCount: number;
  indexCount: number;
  vertexOffset: number;
  normalOffset: number;
  uvOffset: number;
  indexOffset: number;
  vertexColorOffset: number;
  bboxMin: number[];
  bboxMax: number[];
}

interface SCMMaterialDesc {
  textureId: number;
  opacity: number;
  flags: number;
}

interface SCMTextureDesc {
  width: number;
  height: number;
  compression: number;
  dataOffset: number;
  dataSize: number;
  contentHash: number[];
}

interface SCMWorkerResult {
  entityCount: number;
  coordSystem: number;
  color: Uint8Array;
  position: Float32Array;
  rotation: Float32Array;
  scale: Float32Array;
  meshId: Int32Array;
  materialId: Int32Array;
  geometries: SCMGeometryDesc[];
  materials: SCMMaterialDesc[];
  textures: SCMTextureDesc[];
  positionsStream: Float32Array;
  normalsStream: Float32Array | null;
  uvsStream: Float32Array | null;
  indicesStream: Uint32Array;
  vertexColorsStream: Uint8Array | null;
  textureDataPool: Uint8Array | null;
  elapsed: number;
}

// ── Helper: detect SCM URL ──────────────────────────────────────────────

function isSCMUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname;
    return pathname.toLowerCase().endsWith(".scm");
  } catch {
    return url.toLowerCase().endsWith(".scm");
  }
}

// ── Helper: load textures from pool ─────────────────────────────────────

function hashToHex(hash: number[]): string {
  let hex = "";
  for (let i = 0; i < hash.length; i++)
    hex += hash[i].toString(16).padStart(2, "0");
  return hex;
}

async function loadSCMTextures(
  textures: SCMTextureDesc[],
  pool: Uint8Array | null
): Promise<Map<number, Texture>> {
  const texMap = new Map<number, Texture>();
  if (!pool || textures.length === 0) return texMap;

  const hashCache = new Map<string, Texture>();
  const decodeJobs: Promise<void>[] = [];

  for (let i = 0; i < textures.length; i++) {
    const desc = textures[i];
    const hashKey = desc.contentHash
      ? hashToHex(desc.contentHash)
      : `tex_${i}`;

    if (hashCache.has(hashKey)) {
      texMap.set(i, hashCache.get(hashKey)!);
      continue;
    }

    const end = Math.min(desc.dataOffset + desc.dataSize, pool.length);
    if (desc.dataOffset >= pool.length) continue;
    const data = pool.slice(desc.dataOffset, end);

    const tex = new Texture();
    tex.minFilter = LinearFilter;
    tex.magFilter = LinearFilter;
    tex.wrapS = RepeatWrapping;
    tex.wrapT = RepeatWrapping;
    tex.colorSpace = SRGBColorSpace;

    hashCache.set(hashKey, tex);
    texMap.set(i, tex);

    // A single bad texture must not reject the whole build — the model is far
    // more valuable than one map, so log and leave that texture undecoded.
    const onDecodeFailed = (e: any) =>
      console.warn(
        `[DesignManager] texture ${i} decode failed (${desc.width}x${desc.height}, compression=${desc.compression}):`,
        e?.message ?? e
      );

    if (desc.compression === 1 || desc.compression === 2) {
      const blob = new Blob([data], {
        type: desc.compression === 1 ? "image/png" : "image/jpeg",
      });
      decodeJobs.push(
        createImageBitmap(blob)
          .then((bmp) => {
            tex.image = bmp;
            tex.needsUpdate = true;
          })
          .catch(onDecodeFailed)
      );
    } else {
      // Raw RGBA: ImageData throws unless the buffer is exactly w*h*4 bytes.
      const expected = desc.width * desc.height * 4;
      if (desc.width <= 0 || desc.height <= 0 || data.byteLength < expected) {
        console.warn(
          `[DesignManager] texture ${i} raw data too short: got ${data.byteLength}, need ${expected}`
        );
        continue;
      }
      decodeJobs.push(
        (async () => {
          const imgData = new ImageData(
            new Uint8ClampedArray(
              data.buffer,
              data.byteOffset,
              expected
            ),
            desc.width,
            desc.height
          );
          const canvas = new (globalThis as any).OffscreenCanvas(
            desc.width,
            desc.height
          );
          const ctx = canvas.getContext("2d")!;
          (ctx as any).putImageData(imgData, 0, 0);
          const bmp = await createImageBitmap(canvas as any);
          tex.image = bmp;
          tex.needsUpdate = true;
        })().catch(onDecodeFailed)
      );
    }
  }

  if (decodeJobs.length > 0) await Promise.all(decodeJobs);

  // Drop textures that never received an image (skipped or failed to decode) so
  // materials fall back to map: null instead of binding an empty texture.
  for (const [id, tex] of Array.from(texMap)) {
    if (!tex.image) {
      texMap.delete(id);
      tex.dispose();
    }
  }

  return texMap;
}

/** Release decoded bitmaps + GPU handles for textures nothing ended up using. */
function disposeTextures(textures: Iterable<Texture>) {
  for (const tex of textures) {
    (tex.image as ImageBitmap | undefined)?.close?.();
    tex.dispose();
  }
}

// ── Helper: build BufferGeometry from SCM streams ───────────────────────

function buildSCMGeometry(
  geo: SCMGeometryDesc,
  ps: Float32Array,
  ns: Float32Array | null,
  uvsSrc: Float32Array | null,
  idxs: Uint32Array,
  vcStream: Uint8Array | null
): BufferGeometry {
  const geom = new BufferGeometry();
  const vc = geo.vertexCount;

  // Positions
  const vSrc = geo.vertexOffset / 4;
  geom.setAttribute(
    "position",
    new BufferAttribute(ps.slice(vSrc, vSrc + vc * 3), 3)
  );

  // Indices — must be set before computeVertexNormals() below, otherwise three
  // averages normals over an unindexed triangle soup and the shading is wrong.
  const iSrc = geo.indexOffset / 4;
  const indexSlice = idxs.slice(iSrc, iSrc + geo.indexCount);
  geom.setIndex(new BufferAttribute(indexSlice, 1));

  // Normals
  if (geo.normalOffset !== 0xffffffff && ns) {
    const nSrc = geo.normalOffset / 4;
    geom.setAttribute(
      "normal",
      new BufferAttribute(ns.slice(nSrc, nSrc + vc * 3), 3)
    );
  } else {
    geom.computeVertexNormals();
  }

  // UVs
  if (uvsSrc && geo.uvOffset !== 0xffffffff) {
    const uSrc = geo.uvOffset / 4;
    geom.setAttribute(
      "uv",
      new BufferAttribute(uvsSrc.slice(uSrc, uSrc + vc * 2), 2)
    );
  }

  // Vertex colors from stream
  if (vcStream && geo.vertexColorOffset !== 0xffffffff) {
    const vcSrc = geo.vertexColorOffset;
    const colorData = new Float32Array(vc * 3);
    for (let v = 0; v < vc; v++) {
      colorData[v * 3] = vcStream[vcSrc + v * 3] / 255;
      colorData[v * 3 + 1] = vcStream[vcSrc + v * 3 + 1] / 255;
      colorData[v * 3 + 2] = vcStream[vcSrc + v * 3 + 2] / 255;
    }
    geom.setAttribute("color", new BufferAttribute(colorData, 3));
  } else {
    const white = new Float32Array(vc * 3);
    white.fill(1.0);
    geom.setAttribute("color", new BufferAttribute(white, 3));
  }

  return geom;
}

// ── DesignManager ───────────────────────────────────────────────────────

// 保存済み (確定) IFC オブジェクト 1 件分の描画パラメータ。
// 位置は点群ワールド座標。AR 配置 (kho editor) と保存フローが参照する共通型。
export type TSavedIfcObject = {
  id: string;
  url?: string | null;
  posX: number;
  posY: number;
  posZ: number;
  rotX?: number;
  rotY?: number;
  rotZ?: number;
  scale?: number;
};

/**
 * DesignManager — DISPLAY-ONLY feature: load a design IFC via a Web Worker,
 * build a single merged terrain mesh, keep it offset-synced with the point
 * cloud, apply adaptive near/far, and render it into its OWN scene.
 *
 * All AR object placement (placeholder ghost, committed/saved objects,
 * selection, surface picking, gizmo) is owned by the kho editor instance store
 * (see viewer/editor/instanceStore.ts), reached via the Viewer's wrappers.
 * DesignManager does NOT touch the editor store.
 */
@Observable
export class DesignManager {
  ifcLoader: Worker = null as any;
  ifcLoaderReady = false;

  scmWorker: Worker | null = null;
  scmWorkerReady = false;

  scene: Scene = null as any;
  batchedMesh: Mesh | null = null;
  scmGroup: Group | null = null;
  loaderData: any = null;
  groupBoundingBox: Box3 | null = null;
  // IFC base translation (Float64 precision kept outside the geometry)
  meshBasePosition: Vector3 = new Vector3();

  // Per-slot bboxes; groupBoundingBox = their union (camera fit / bound offset)
  private ifcBBox: Box3 | null = null;
  private scmBBox: Box3 | null = null;

  // SCM load token — stale worker results are dropped (IFC has no token protocol)
  private scmToken = 0;

  // React remount (or StrictMode) calls viewer.Init → designManager.init more
  // than once. Without this guard, lights + workers accumulate each time.
  private initialized = false;

  constructor(private viewer: Viewer) {}

  init() {
    if (this.initialized) {
      devLog("DesignManager.init.skipped", { reason: "already initialized" });
      return;
    }
    this.initialized = true;

    // DesignManager keeps its OWN scene for the terrain (the editor instances
    // render into the Viewer-owned design scene, a separate pass).
    if (!this.scene) {
      this.scene = new Scene();
    }
    const { viewer } = this;

    // IFC Worker
    try {
      this.ifcLoader = new Worker("/ifc-load-worker.js");
    } catch (e: any) {
      console.error("[DesignManager] Failed to create IFC Worker:", e.message);
    }

    // SCM Worker
    try {
      this.scmWorker = new Worker("/scm-load-worker.js");
    } catch (e: any) {
      console.error("[DesignManager] Failed to create SCM Worker:", e.message);
    }

    // Lights for the terrain scene.
    const directionalLight = new DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(1, 2, 3);
    this.scene.add(directionalLight);
    const secondirectionalLight = new DirectionalLight(0xffffff, 1);
    secondirectionalLight.position.set(-1, -1, -1);
    this.scene.add(secondirectionalLight);
    const topLight = new DirectionalLight(0xffffff, 0.8);
    topLight.position.set(0, 0, 1);
    this.scene.add(topLight);

    const ambientLight = new AmbientLight(0xffffff, 2.5);
    this.scene.add(ambientLight);

    // IFC Worker message handler
    if (this.ifcLoader) {
      this.ifcLoader.onmessage = ({ data }) => {
        if (data.type === "ready") {
          this.ifcLoaderReady = true;
          return;
        } else if (data.type === "error") {
          console.error("[DesignManager] IFC Worker error:", data.error);
          this.setDesignStatus("error");
          return;
        } else {
          if (!!data.data) {
            this.setDesignStatus("loaded");
            this.loaderData = data.data;
          } else if (data.error) {
            console.error(
              "[DesignManager] IFC Worker returned error:",
              data.error
            );
            this.setDesignStatus("error");
          }
        }
      };

      this.ifcLoader.onerror = (error) => {
        console.error("[DesignManager] IFC Worker.onerror:", error);
        this.setDesignStatus("error");
      };
    }

    // SCM Worker message handler
    if (this.scmWorker) {
      this.scmWorker.onmessage = ({ data }) => {
        devLog("DesignManager.scmWorker.onmessage", {
          type: data.type,
          token: data.token,
          activeToken: this.scmToken,
        });
        if (data.type === "ready") {
          this.scmWorkerReady = true;
          return;
        }
        // Token gate — drop results of superseded loads
        if (data.token !== this.scmToken) {
          devLog("DesignManager.scmWorker.rejected-stale", {
            incomingToken: data.token,
            activeToken: this.scmToken,
            type: data.type,
          });
          return;
        }
        if (data.type === "error") {
          console.error("[DesignManager] SCM Worker error:", data.error);
          devLog("DesignManager.scmWorker.error", { error: data.error });
          this.setScmStatus("error");
          return;
        } else if (data.type === "loaded") {
          console.log(
            `[DesignManager] SCM parsed in ${data.result.elapsed?.toFixed(1)}ms, ${data.result.entityCount} entities`
          );
          devLog("DesignManager.scmWorker.loaded", {
            elapsedMs: data.result.elapsed,
            entityCount: data.result.entityCount,
            geometryCount: data.result.geometries?.length,
            materialCount: data.result.materials?.length,
            textureCount: data.result.textures?.length,
            token: data.token,
          });
          this.setScmStatus("loaded");
          this.buildSCM(data.result);
        }
      };

      this.scmWorker.onerror = (error) => {
        console.error("[DesignManager] SCM Worker.onerror:", error);
        devLog("DesignManager.scmWorker.onerror", { message: error.message });
        this.setScmStatus("error");
      };
    }

    // Setup observers
    observe(this, ["loaderData"], () => {
      this.build();
    });

    observe(
      viewer,
      ["designOffsetMode", "designBoundOffset", "cloudPointOffset"],
      () => {
        this.syncOffset();
      }
    );

    // ── Load triggers — one per lane; ready-observers re-run for early URLs ──
    observe(viewer, ["designURL"], this.handleDesignURLChange);
    observe(this, ["ifcLoaderReady"], this.handleDesignURLChange);
    observe(viewer, ["scmURL"], this.handleScmURLChange);
    observe(this, ["scmWorkerReady"], this.handleScmURLChange);

    // Per-lane show/hide — each flag drives only its own object.
    observe(viewer, ["designVisible"], () => {
      if (this.batchedMesh) this.batchedMesh.visible = viewer.designVisible;
    });
    observe(viewer, ["scmVisible"], () => {
      if (this.scmGroup) this.scmGroup.visible = viewer.scmVisible;
    });
  }

  // ── Load handlers — each disposes only ITS OWN object, so IFC & SCM coexist ──

  handleDesignURLChange = () => {
    const url = this.viewer.designURL;
    devLog("DesignManager.handleDesignURLChange", {
      hasURL: !!url,
      urlPreview: url ? String(url).slice(0, 120) : null,
      ifcLoaderReady: this.ifcLoaderReady,
    });
    // This road is IFC-only — .scm belongs to viewer.scmURL, ignore it here
    if (url && isSCMUrl(url)) {
      devLog("DesignManager.handleDesignURLChange.scmIgnored", {});
      return;
    }
    this.disposeIfc();
    if (!url) {
      this.setDesignStatus("blank");
      return;
    }
    if (!this.ifcLoaderReady) {
      devLog("DesignManager.handleDesignURLChange.notReady", {});
      return; // the ifcLoaderReady observer will call us again
    }
    this.setDesignStatus("loading");
    try {
      this.ifcLoader.postMessage({ url });
    } catch (e: any) {
      console.error("[DesignManager] IFC postMessage failed:", e.message);
      this.setDesignStatus("error");
    }
  };

  handleScmURLChange = () => {
    const url = this.viewer.scmURL;
    // Bump first — any in-flight SCM result becomes stale
    this.scmToken++;
    const token = this.scmToken;
    devLog("DesignManager.handleScmURLChange", {
      hasURL: !!url,
      urlPreview: url ? String(url).slice(0, 120) : null,
      scmWorkerReady: this.scmWorkerReady,
      token,
    });
    this.disposeScm();
    if (!url) {
      this.setScmStatus("blank");
      return;
    }
    if (!this.scmWorkerReady) {
      devLog("DesignManager.handleScmURLChange.notReady", {});
      return; // the scmWorkerReady observer will call us again
    }
    this.setScmStatus("loading");
    try {
      devLog("DesignManager.scmWorker.postMessage", { kind: "load", token });
      this.scmWorker!.postMessage({ type: "load", url, token });
    } catch (e: any) {
      console.error("[DesignManager] SCM postMessage failed:", e.message);
      devLog("DesignManager.scmWorker.postMessage.failed", { error: e.message });
      this.setScmStatus("error");
    }
  };

  private setDesignStatus(s: "blank" | "loading" | "loaded" | "error") {
    this.viewer.designStatus = s;
  }

  private setScmStatus(s: "blank" | "loading" | "loaded" | "error") {
    this.viewer.scmStatus = s;
  }


  syncOffset() {
    const offset =
      this.viewer.designOffsetMode === "cloudpoint"
        ? this.viewer.cloudPointOffset
        : this.viewer.designBoundOffset;

    const offsetVec = new Vector3(offset[0], offset[1], offset[2]);

    // IFC keeps its Float64 base translation outside the geometry
    if (this.batchedMesh) {
      this.batchedMesh.position.set(
        offsetVec.x + this.meshBasePosition.x,
        offsetVec.y + this.meshBasePosition.y,
        offsetVec.z + this.meshBasePosition.z
      );
    }
    if (this.scmGroup) {
      this.scmGroup.position.copy(offsetVec);
    }
  }

  // ── IFC Build (existing) ──────────────────────────────────────────────

  build() {
    if (!this.loaderData) return;
    this.disposeIfc(); // previous IFC only — the SCM slot stays on screen

    const meshData = this.loaderData as {
      vertices: Float32Array;
      normals: Float32Array;
      indices: Uint32Array;
      translation: [number, number, number];
      color: { r: number; g: number; b: number; a: number };
    }[];

    // Calculate totals for merged geometry
    let totalVertices = 0;
    let totalIndices = 0;
    for (const data of meshData) {
      totalVertices += data.vertices.length / 3;
      totalIndices += data.indices.length;
    }

    // Pre-allocate merged arrays
    const mergedPositions = new Float32Array(totalVertices * 3);
    const mergedNormals = new Float32Array(totalVertices * 3);
    const mergedColors = new Float32Array(totalVertices * 3);
    const mergedIndices = new Uint32Array(totalIndices);

    let vertexOffset = 0;
    let indexOffset = 0;

    const minBrightness = 0.4;

    // Use first mesh's translation as base offset (Float64 precision)
    const baseTranslation = meshData[0].translation || [0, 0, 0];

    for (let i = 0; i < meshData.length; i++) {
      const data = meshData[i];
      const vertexCount = data.vertices.length / 3;
      const t = data.translation || [0, 0, 0];

      // Copy positions, applying relative translation delta for non-base meshes
      const dx = t[0] - baseTranslation[0];
      const dy = t[1] - baseTranslation[1];
      const dz = t[2] - baseTranslation[2];

      if (dx === 0 && dy === 0 && dz === 0) {
        mergedPositions.set(data.vertices, vertexOffset * 3);
      } else {
        for (let k = 0; k < vertexCount; k++) {
          mergedPositions[(vertexOffset + k) * 3] = data.vertices[k * 3] + dx;
          mergedPositions[(vertexOffset + k) * 3 + 1] =
            data.vertices[k * 3 + 1] + dy;
          mergedPositions[(vertexOffset + k) * 3 + 2] =
            data.vertices[k * 3 + 2] + dz;
        }
      }
      mergedNormals.set(data.normals, vertexOffset * 3);

      // Copy indices with offset
      for (let j = 0; j < data.indices.length; j++) {
        mergedIndices[indexOffset + j] = data.indices[j] + vertexOffset;
      }

      // Set vertex colors (boost dark colors so they receive light)
      const hasValidColor =
        data.color &&
        typeof data.color.r === "number" &&
        typeof data.color.g === "number" &&
        typeof data.color.b === "number";

      const r = Math.max(hasValidColor ? data.color.r : 0.5, minBrightness);
      const g = Math.max(hasValidColor ? data.color.g : 0.5, minBrightness);
      const b = Math.max(hasValidColor ? data.color.b : 0.5, minBrightness);

      for (let j = 0; j < vertexCount; j++) {
        mergedColors[(vertexOffset + j) * 3] = r;
        mergedColors[(vertexOffset + j) * 3 + 1] = g;
        mergedColors[(vertexOffset + j) * 3 + 2] = b;
      }

      vertexOffset += vertexCount;
      indexOffset += data.indices.length;
    }

    // Create single merged geometry
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(mergedPositions, 3)
    );
    geometry.setAttribute(
      "normal",
      new Float32BufferAttribute(mergedNormals, 3)
    );
    geometry.setAttribute("color", new Float32BufferAttribute(mergedColors, 3));
    geometry.setIndex(new Uint32BufferAttribute(mergedIndices, 1));

    // Rotate geometry (IFC Y-up to Z-up)
    geometry.rotateX(Math.PI / 2);
    geometry.computeBoundingBox();

    const material = new MeshLambertMaterial({
      vertexColors: true,
      side: DoubleSide,
      transparent: true,
      opacity: 0.7,
      depthWrite: true,
    });

    const mesh = new Mesh(geometry, material);

    // Apply base translation to mesh position (Float64 precision)
    // After rotateX(PI/2): IFC(x,y,z) -> Three(x,-z,y)
    this.meshBasePosition = new Vector3(
      baseTranslation[0],
      -baseTranslation[2],
      baseTranslation[1]
    );
    mesh.position.copy(this.meshBasePosition);

    this.scene.add(mesh);
    mesh.visible = this.viewer.designVisible; // respect the current toggle
    this.batchedMesh = mesh;

    // Slot bbox in world coords (local box + base translation) → union bbox
    this.ifcBBox = (geometry.boundingBox || new Box3())
      .clone()
      .translate(this.meshBasePosition);
    this.updateUnionBBox();
    this.calculateBoundOffset();

    // Apply current offset mode (don't change mode here, let UI control it)
    this.syncOffset();
  }

  // ── SCM Build ─────────────────────────────────────────────────────────

  async buildSCM(result: SCMWorkerResult) {
    // Defensive vs duplicate "loaded" messages; previous SCM only — IFC stays
    this.disposeScm();

    const ps = result.positionsStream;
    const ns = result.normalsStream;
    const uvsSrc = result.uvsStream;
    const idxs = result.indicesStream;
    if (!ps || !idxs) {
      console.error("[DesignManager] SCM missing position/index streams");
      this.setScmStatus("error");
      return;
    }

    // Decode textures
    const texMap = await loadSCMTextures(
      result.textures,
      result.textureDataPool
    );

    // Build geometry cache: meshId -> BufferGeometry
    const geoCache = new Map<number, BufferGeometry>();
    for (let i = 0; i < result.geometries.length; i++) {
      const geo = result.geometries[i];
      if (geo.vertexCount === 0 || geo.indexCount === 0) continue;
      geoCache.set(
        i,
        buildSCMGeometry(
          geo,
          ps,
          ns,
          uvsSrc,
          idxs,
          result.vertexColorsStream
        )
      );
    }

    // Group entities by (meshId, texId, isTransparent)
    const groups = new Map<string, number[]>();

    for (let ei = 0; ei < result.entityCount; ei++) {
      const mid = result.meshId[ei];
      if (mid < 0 || mid >= result.geometries.length) continue;
      if (!geoCache.has(mid)) continue;

      const alpha = result.color[ei * 4 + 3];
      const isTrans = alpha > 0 && alpha < 255;
      const matId = result.materialId[ei];
      const texId =
        matId >= 0 && matId < result.materials.length
          ? result.materials[matId].textureId
          : -1;
      const key = `${mid}_${texId}_${isTrans ? 1 : 0}`;

      let arr = groups.get(key);
      if (!arr) {
        arr = [];
        groups.set(key, arr);
      }
      arr.push(ei);
    }

    // Create InstancedMesh per group using MeshLambertMaterial
    const group = new Group();
    group.rotation.x = Math.PI / 2;
    const _mat4 = new Matrix4();
    const _quat = new Quaternion();
    const _pos = new Vector3();
    const _scl = new Vector3();
    const _col = new Color();

    for (const [key, entityIds] of groups) {
      const parts = key.split("_");
      const meshId = parseInt(parts[0], 10);
      const texId = parseInt(parts[1], 10);
      const isTrans = parts[2] === "1";
      const count = entityIds.length;
      const geo = result.geometries[meshId];

      const hasTexture = texId >= 0 && texMap.has(texId);

      const mat = new MeshLambertMaterial({
        vertexColors: true,
        side: DoubleSide,
        transparent: isTrans,
        depthWrite: !isTrans,
        map: hasTexture ? texMap.get(texId)! : null,
      });

      const geometry = geoCache.get(meshId)!;
      const mesh = new InstancedMesh(geometry.clone(), mat, count);

      const hasVtxColors =
        result.vertexColorsStream !== null &&
        geo.vertexColorOffset !== 0xffffffff;

      for (let i = 0; i < count; i++) {
        const ei = entityIds[i];

        // Transform: TRS -> Matrix4
        const pi = ei * 3;
        const ri = ei * 4;
        const si = ei * 3;
        _pos.set(
          result.position[pi] || 0,
          result.position[pi + 1] || 0,
          result.position[pi + 2] || 0
        );
        _quat.set(
          result.rotation[ri] || 0,
          result.rotation[ri + 1] || 0,
          result.rotation[ri + 2] || 0,
          isNaN(result.rotation[ri + 3]) ? 1 : result.rotation[ri + 3]
        );
        _scl.set(
          result.scale[si] || 1,
          result.scale[si + 1] || 1,
          result.scale[si + 2] || 1
        );
        _mat4.compose(_pos, _quat, _scl);
        mesh.setMatrixAt(i, _mat4);

        // Skip setColorAt when geometry already has per-vertex colors; the
        // attribute color carries the data and instanceColor would just
        // multiply. Only use entity color when vertex stream is absent.
        if (!hasVtxColors) {
          const r = result.color[ei * 4];
          const g = result.color[ei * 4 + 1];
          const b = result.color[ei * 4 + 2];
          const a = result.color[ei * 4 + 3];
          const isBlank = r === 0 && g === 0 && b === 0 && a === 0;
          const isWhite = r >= 254 && g >= 254 && b >= 254;
          if (isBlank || isWhite) {
            _col.setRGB(0.7, 0.7, 0.7);
          } else {
            _col.setRGB(r / 255, g / 255, b / 255);
          }
          mesh.setColorAt(i, _col);
        }
      }

      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.frustumCulled = false;
      mesh.renderOrder = isTrans ? 1 : 0;

      group.add(mesh);
    }


    // Compute bounding box
    const overallBox = new Box3().setFromObject(group);

    // groupBoundingBox must be assigned BEFORE scmGroup: the observer on
    // scmGroup runs calculateBoundOffset(), which reads groupBoundingBox and
    // bails out early while it is still null.
    this.scene.add(group);
    group.visible = this.viewer.scmVisible; // respect the current toggle
    this.scmGroup = group;

    // Slot bbox → union bbox (camera fit / bound offset cover both)
    this.scmBBox = overallBox;
    this.updateUnionBBox();
    this.calculateBoundOffset();

    // (centres below are only for the log line)
    const centerX = (overallBox.min.x + overallBox.max.x) / 2;
    const centerY = (overallBox.min.y + overallBox.max.y) / 2;
    const centerZ = (overallBox.min.z + overallBox.max.z) / 2;

    console.log(
      `[DesignManager] SCM built: ${groups.size} draw calls, bbox center=[${centerX.toFixed(1)}, ${centerY.toFixed(1)}, ${centerZ.toFixed(1)}]`
    );
    devLog("DesignManager.buildSCM.done", {
      drawCalls: groups.size,
      bboxMin: [overallBox.min.x, overallBox.min.y, overallBox.min.z].map((v) => Number(v.toFixed(2))),
      bboxMax: [overallBox.max.x, overallBox.max.y, overallBox.max.z].map((v) => Number(v.toFixed(2))),
      groupChildren: group.children.length,
    });

    this.syncOffset();
  }

  // ── Disposal — per-slot; disposeScene() is the nuclear option (unload only) ──

  /** Dispose one mesh deeply: geometry, materials and their textures. */
  private static disposeMeshDeep(obj: any, seenTextures?: Set<any>) {
    const seen = seenTextures ?? new Set<any>();
    if (obj?.geometry?.dispose) obj.geometry.dispose();
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const m of mats) {
      if (!m) continue;
      if (m.map && !seen.has(m.map)) {
        seen.add(m.map);
        m.map.dispose();
      }
      m.dispose?.();
    }
    obj.dispose?.();
  }

  /** Drop only the IFC mesh — the SCM design stays on screen. */
  private disposeIfc() {
    if (this.batchedMesh) {
      this.scene.remove(this.batchedMesh);
      DesignManager.disposeMeshDeep(this.batchedMesh);
      this.batchedMesh = null;
    }
    this.meshBasePosition = new Vector3();
    this.ifcBBox = null;
    this.updateUnionBBox();
  }

  /** Drop only the SCM group — the IFC design stays on screen. */
  private disposeScm() {
    if (this.scmGroup) {
      const seen = new Set<any>();
      this.scmGroup.traverse((obj) => {
        if (obj instanceof InstancedMesh) {
          DesignManager.disposeMeshDeep(obj, seen);
        }
      });
      this.scene.remove(this.scmGroup);
      this.scmGroup.clear();
      this.scmGroup = null;
    }
    this.scmBBox = null;
    this.updateUnionBBox();
  }

  /** groupBoundingBox = union of the loaded slots (camera fit / bound offset). */
  private updateUnionBBox() {
    const boxes = [this.ifcBBox, this.scmBBox].filter(Boolean) as Box3[];
    if (boxes.length === 0) {
      this.groupBoundingBox = null;
      return;
    }
    const union = boxes[0].clone();
    for (let i = 1; i < boxes.length; i++) union.union(boxes[i]);
    this.groupBoundingBox = union;
  }

  /** NUCLEAR clear: both slots + orphan sweep. Only unload() uses it. */
  private disposeScene() {
    const renderer = (this.viewer as any)._renderer as
      | WebGLRenderer
      | undefined;
    const memBefore = renderer
      ? {
          geometries: renderer.info.memory.geometries,
          textures: renderer.info.memory.textures,
          programs: renderer.info.programs?.length ?? 0,
        }
      : null;
    let disposedInstancedMeshes = 0;
    let disposedBatched = 0;
    let disposedStrayMeshes = 0;
    const disposedTextures = new Set<any>();

    const disposeMesh = (obj: any) =>
      DesignManager.disposeMeshDeep(obj, disposedTextures);

    // Tracked refs
    if (this.batchedMesh) {
      this.scene.remove(this.batchedMesh);
      disposeMesh(this.batchedMesh);
      disposedBatched++;
      this.batchedMesh = null;
    }
    if (this.scmGroup) {
      this.scmGroup.traverse((obj) => {
        if (obj instanceof InstancedMesh) {
          disposeMesh(obj);
          disposedInstancedMeshes++;
        }
      });
      this.scene.remove(this.scmGroup);
      this.scmGroup.clear();
      this.scmGroup = null;
    }

    // Stray orphans: scan scene.children for InstancedMesh / BatchedMesh /
    // Groups added by previous build runs that lost their tracking reference.
    const strays: any[] = [];
    this.scene.traverse((obj) => {
      if (obj === this.scene) return;
      if (obj instanceof InstancedMesh || (obj as any).isBatchedMesh) {
        strays.push(obj);
      }
    });
    for (const obj of strays) {
      disposeMesh(obj);
      obj.parent?.remove(obj);
      disposedStrayMeshes++;
    }

    this.ifcBBox = null;
    this.scmBBox = null;
    this.groupBoundingBox = null;

    if (renderer) {
      devLog("DesignManager.disposeScene", {
        before: memBefore,
        after: {
          geometries: renderer.info.memory.geometries,
          textures: renderer.info.memory.textures,
          programs: renderer.info.programs?.length ?? 0,
        },
        disposedInstancedMeshes,
        disposedBatched,
        disposedStrayMeshes,
        disposedTextures: disposedTextures.size,
      });
    }
  }

  /** Full unload of BOTH designs (project switch / leaving the view). */
  unload() {
    this.disposeScene();
    this.setDesignStatus("blank");
    this.setScmStatus("blank");
  }

  calculateBoundOffset() {
    if (!this.groupBoundingBox) return;
    const bbox = this.groupBoundingBox;
    const centerX = (bbox.min.x + bbox.max.x) / 2;
    const centerY = (bbox.min.y + bbox.max.y) / 2;
    const centerZ = (bbox.min.z + bbox.max.z) / 2;
    console.log(
      "Setting manual offset to center:",
      -centerX,
      -centerY,
      -centerZ
    );

    this.viewer.designBoundOffset = [-centerX, -centerY, -centerZ];
  }

  private _viewDir = new Vector3();
  private _toCorner = new Vector3();

  private computeAdaptiveNearFar(
    camera: Camera,
  ): { near: number; far: number } | null {
    if (!this.batchedMesh || !this.groupBoundingBox) return null;

    // World-space bounding box of the IFC 地形 (terrain). AR objects rest on this
    // surface and share this clip envelope (see getDesignClip), so terrain-only
    // is sufficient here.
    const worldBox = new Box3();
    worldBox.expandByObject(this.batchedMesh);
    if (worldBox.isEmpty()) return null;

    // Camera view direction (points into scene, i.e. -Z in camera local space)
    const viewDir = camera.getWorldDirection(this._viewDir);
    const cameraPos = camera.position;

    // Project all 8 corners onto view direction to get signed depth
    const xs = [worldBox.min.x, worldBox.max.x];
    const ys = [worldBox.min.y, worldBox.max.y];
    const zs = [worldBox.min.z, worldBox.max.z];

    let minDepth = Infinity;
    let maxDepth = -Infinity;
    for (const x of xs) {
      for (const y of ys) {
        for (const z of zs) {
          this._toCorner.set(x - cameraPos.x, y - cameraPos.y, z - cameraPos.z);
          const depth = this._toCorner.dot(viewDir);
          if (depth < minDepth) minDepth = depth;
          if (depth > maxDepth) maxDepth = depth;
        }
      }
    }

    // If entire box is behind camera, skip adaptive clipping
    if (maxDepth <= 0) return null;

    // Ortho: allow negative near (no perspective divide, "behind camera" is valid)
    // Perspective: near must be > 0
    const isOrtho = !!(camera as any).isOrthographicCamera;
    const near = isOrtho ? minDepth : Math.max(minDepth, 0.01);
    const far = Math.max(maxDepth, near + 0.01);

    return { near, far };
  }

  // Adaptive near/far for the design terrain — the Viewer passes this to the
  // editor instance store (getInstanceStore host.getDesignClip) so the AR
  // objects render with the same clip envelope as the terrain they sit on.
  getDesignClip(camera: Camera): { near: number; far: number } | null {
    return this.batchedMesh?.visible ? this.computeAdaptiveNearFar(camera) : null;
  }

  render(renderer: WebGLRenderer, camera: Camera) {
    if (!this.scene) return;

    // Adaptive near/far for design scene
    const cam = camera as any;
    const adaptiveClip = this.batchedMesh?.visible
      ? this.computeAdaptiveNearFar(camera)
      : null;
    let savedNear: number | undefined;
    let savedFar: number | undefined;

    if (adaptiveClip && cam.near !== undefined) {
      savedNear = cam.near;
      savedFar = cam.far;
      cam.near = adaptiveClip.near;
      cam.far = adaptiveClip.far;
      cam.updateProjectionMatrix();
    }

    // Clear only depth buffer before rendering design scene.
    // This fixes iOS Safari issue where depth from previous scenes blocks this one.
    renderer.clearDepth();
    renderer.render(this.scene, camera);

    // Restore original near/far
    if (savedNear !== undefined) {
      cam.near = savedNear;
      cam.far = savedFar;
      cam.updateProjectionMatrix();
    }
  }
}
